// src/shared/components/PublishModal.tsx

import { useState, useMemo } from 'react';
import {
  Modal, Stack, Select, TextInput, Button, Group, CopyButton,
  Text, Divider, ActionIcon, Tooltip, AspectRatio, SegmentedControl, Center, Loader,
} from '@mantine/core';
import { IconCopy, IconCheck } from '@tabler/icons-react';
import { MediaPlayerLinker } from '@lib2/model/player/PlayerLinker';
import { MediaTypeUtil } from '@lib2/model/media/MediaType';
import { useResellerBrand } from '@/shared/hooks/useResellerBrand';
import { usePlayers, setStoredPlayerId } from '@/features/players/usePlayers';
import { MonetizeUtils } from '@/features/monetize/MonetizeUtils';
import { usePreviewGrant } from '@/features/monetize/usePreviewGrant';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { EMBED_SIZES, PUBLISH_LINK_FORMATS, type EmbedSize, type PublishLinkFormat } from '@/lib/constants/embed';
import { STORAGE_KEYS } from '@/lib/constants/storage';
import type { IMediaViewData } from '@lib2/model/media/IMediaData';
import type IMediaView from '@lib2/model/media/IMediaView';

interface PublishModalProps {
  opened: boolean;
  onClose: () => void;
  media: IMediaViewData;
}

const SIZE_OPTIONS = Object.entries(EMBED_SIZES).map(([key, val]) => ({
  value: key,
  label: val.label,
}));

const MONO_STYLE = { input: { fontFamily: 'monospace', fontSize: 12 } } as const;

/** Get stored link-format preference from localStorage, scoped to current customer. */
function getStoredLinkFormat(cId: number | null): PublishLinkFormat | null {
  if (!cId) return null;
  try {
    const stored = localStorage.getItem(`${STORAGE_KEYS.PUBLISH_LINK_FORMAT}_${cId}`);
    return PUBLISH_LINK_FORMATS.includes(stored as PublishLinkFormat)
      ? (stored as PublishLinkFormat)
      : null;
  } catch {
    return null;
  }
}

/** Save link-format preference to localStorage, scoped to customer. */
function setStoredLinkFormat(cId: number, format: PublishLinkFormat): void {
  try {
    localStorage.setItem(`${STORAGE_KEYS.PUBLISH_LINK_FORMAT}_${cId}`, format);
  } catch {
    // localStorage may be unavailable
  }
}

/** Read-only field with integrated one-click copy icon. */
function CopyableField({ label, value }: { label: string; value: string }) {
  return (
    <TextInput
      label={label}
      value={value}
      readOnly
      styles={MONO_STYLE}
      rightSectionWidth={36}
      rightSection={
        <CopyButton value={value}>
          {({ copied, copy }) => (
            <Tooltip label={copied ? 'Copied' : 'Copy'} withArrow position="left">
              <ActionIcon
                variant="subtle"
                color={copied ? 'teal' : 'gray'}
                onClick={copy}
                size="sm"
              >
                {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
              </ActionIcon>
            </Tooltip>
          )}
        </CopyButton>
      }
    />
  );
}

export function PublishModal({ opened, onClose, media }: PublishModalProps) {
  const { selectedCId, selectedCustomer } = useAuth();
  const { defaultPlayerId, playerOptions, getPlayerById } = usePlayers();
  const isReseller = useResellerBrand();
  const { grant: previewGrant, ready: previewReady } = usePreviewGrant(`media:${media.media.mediaId}`);

  const [selectedPlayerId, setSelectedPlayerId] = useState<number | null>(null);
  const [sizeKey, setSizeKey] = useState<EmbedSize>('medium');
  const [sizeMode, setSizeMode] = useState<'responsive' | 'fixed'>('responsive');

  // Reference-ID links: only offered when the media has a refId. Preference is
  // per customer; media without a refId always uses media-ID links.
  const hasRefId = !!media.media.refId;
  const [linkFormat, setLinkFormat] = useState<PublishLinkFormat>(() =>
    hasRefId && getStoredLinkFormat(selectedCId) === 'refId' ? 'refId' : 'mediaId'
  );
  const useRefId = hasRefId && linkFormat === 'refId';

  // Resolve effective player: local selection > hook default
  const effectivePlayerId = selectedPlayerId ?? defaultPlayerId;
  const selectedPlayer = effectivePlayerId ? getPlayerById(effectivePlayerId) : undefined;

  const isLive = MediaTypeUtil.isLive(media.media.mediaType);
  const allowDownload = selectedPlayer?.settings.allowDownload ?? false;
  const allowHlsLink = selectedCustomer?.cfg.quota.features.account.allowHlsLink ?? false;

  // Build URLs when player is available
  const urls = useMemo(() => {
    if (!effectivePlayerId) return null;

    const mediaView = { data: media } as unknown as IMediaView;
    const linker = new MediaPlayerLinker(effectivePlayerId, mediaView, isReseller);

    const size = EMBED_SIZES[sizeKey];
    const wh = { width: size.width, height: size.height };

    return {
      embedCode: linker.getIframe(useRefId, sizeMode === 'responsive', wh),
      previewUrl: linker.getPreviewUrl(useRefId, { isPreview: true }),
      directUrl: !isLive ? linker.getFileUrl(useRefId) : null,
      downloadUrl: !isLive ? linker.getFileUrl(useRefId, { download: true }) : null,
      hlsUrl: linker.getHlsUrl(useRefId),
    };
  }, [effectivePlayerId, media, sizeKey, sizeMode === 'responsive', isLive, useRefId, isReseller]);

  const handlePlayerChange = (val: string | null) => {
    if (!val) return;
    const id = parseInt(val, 10);
    setSelectedPlayerId(id);
    if (selectedCId) setStoredPlayerId(selectedCId, id);
  };

  const handleLinkFormatChange = (val: string) => {
    setLinkFormat(val as PublishLinkFormat);
    if (selectedCId) setStoredLinkFormat(selectedCId, val as PublishLinkFormat);
  };

  if (!effectivePlayerId || !urls) return null;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Publish & Embed"
      size="lg"
    >
      <Stack gap="md">
        {/* Player selector */}
        <Select
          label="Player"
          data={playerOptions}
          value={String(effectivePlayerId)}
          onChange={handlePlayerChange}
          allowDeselect={false}
        />

        {/* Link format — only offered when the media has a Reference ID */}
        {hasRefId && (
          <Group justify="space-between" align="center">
            <div>
              <Text size="sm" fw={500}>Link Format</Text>
              <Text size="xs" c="dimmed">Reference ID: {media.media.refId}</Text>
            </div>
            <SegmentedControl
              value={linkFormat}
              onChange={handleLinkFormatChange}
              data={[
                { value: 'mediaId', label: 'Media ID' },
                { value: 'refId', label: 'Reference ID' },
              ]}
              size="sm"
            />
          </Group>
        )}

        {/* Player preview */}
        <AspectRatio ratio={16 / 9} maw={480} mx="auto" w="100%">
          {previewReady ? <iframe
            src={MonetizeUtils.withPreviewGrant(urls.previewUrl, previewGrant)}
            style={{ border: 'none', borderRadius: 6, background: 'var(--mantine-color-gray-1)' }}
            allowFullScreen
            title={media.media.title}
          /> : <Center style={{ background: 'var(--mantine-color-gray-1)', borderRadius: 6 }}><Loader size="lg" /></Center>}
        </AspectRatio>

        <Divider />

        {/* Embed sizing */}
        <Stack gap="xs">
          <Text size="sm" fw={500}>Embed Size</Text>
          <Group gap="md" align="center">
            <SegmentedControl
              value={sizeMode}
              onChange={(val) => setSizeMode(val as 'responsive' | 'fixed')}
              data={[
                { value: 'responsive', label: 'Responsive' },
                { value: 'fixed', label: 'Fixed Size' },
              ]}
              size="sm"
            />
            {sizeMode === 'fixed' && (
              <Select
                data={SIZE_OPTIONS}
                value={sizeKey}
                onChange={(val) => val && setSizeKey(val as EmbedSize)}
                allowDeselect={false}
                w={220}
                size="sm"
              />
            )}
          </Group>
        </Stack>

        {/* Embed code */}
        <CopyableField label="Embed Code" value={urls.embedCode} />

        <Divider />

        {/* Links section */}
        <Text size="sm" fw={600} c="dimmed">Links</Text>

        <CopyableField label="Preview URL" value={urls.previewUrl} />

        {urls.directUrl && (
          <CopyableField label="Direct File Link" value={urls.directUrl} />
        )}

        {urls.downloadUrl && allowDownload && (
          <CopyableField label="Download Link" value={urls.downloadUrl} />
        )}

        {allowHlsLink && urls.hlsUrl && (
          <CopyableField label="HLS Stream URL" value={urls.hlsUrl} />
        )}

        <Group justify="flex-end" mt="xs">
          <Button variant="default" onClick={onClose}>Close</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
